// Time of day (docs/19 N1–N3). An encounter happens at an hour 0–24; the hour decides the
// ambient colour every unlit square is multiplied by, where the sun (or moon) stands and how long
// the shadows are, how strong daylight through an open exit is, and which atmosphere particles
// fly. Everything is a pure function of (hour, climate), so a party of four sees one picture.
//
// Legibility is the hard rule (N2): no keyframe takes the ambient below `AMBIENT_FLOOR` in any
// channel or `LUMA_FLOOR` in luminance, and units are never darkened below `UNIT_FLOOR`
// (lighting.test.ts walks every kit at every half hour).
import { cellHash } from '../tiling/hash';

export type RGB = readonly [number, number, number];
export type Phase = 'night' | 'dawn' | 'day' | 'golden' | 'dusk';
export type Setting = 'indoor' | 'outdoor';

/** No square is multiplied darker than this per channel, or than LUMA_FLOOR in luminance. */
export const AMBIENT_FLOOR = 0.36;
export const LUMA_FLOOR = 0.46;
/** Units take at most this much of the local darkness (1 = never darkened). */
export const UNIT_FLOOR = 0.86;

export const luma = (c: RGB): number => 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
export const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
export const scale = (a: RGB, k: number): RGB => [a[0] * k, a[1] * k, a[2] * k];

/** Lift a colour until it meets both floors (keeps its hue). */
export function legible(c: RGB): RGB {
  let out: RGB = [Math.max(AMBIENT_FLOOR, c[0]), Math.max(AMBIENT_FLOOR, c[1]), Math.max(AMBIENT_FLOOR, c[2])];
  const l = luma(out);
  if (l < LUMA_FLOOR) out = mix(out, [1, 1, 1], (LUMA_FLOOR - l) / (1 - l));
  return out;
}

/** One sky keyframe: what open air looks like at `hour`. */
interface SkyKey {
  hour: number;
  /** Multiply colour of unlit ground outdoors. */
  ambient: RGB;
  /** Colour and strength of daylight spilling through an exit into a room. */
  sky: RGB; skyStrength: number;
  /** Grading: saturation offset (−1..1) and warmth (−1 cool .. 1 warm). */
  saturation: number; warmth: number;
  /** How dark an outdoor shadow is (0 none .. 1 black) — moonlight casts faint ones. */
  shadow: number;
}

// Keyframes round the clock; values between them interpolate (wrapping at 24).
const SKY: readonly SkyKey[] = [
  { hour: 0, ambient: [0.47, 0.54, 0.82], sky: [0.55, 0.65, 1.0], skyStrength: 0.25, saturation: -0.3, warmth: -0.5, shadow: 0.16 },
  { hour: 4.5, ambient: [0.48, 0.55, 0.82], sky: [0.55, 0.65, 1.0], skyStrength: 0.25, saturation: -0.3, warmth: -0.5, shadow: 0.14 },
  { hour: 6, ambient: [0.86, 0.62, 0.66], sky: [1.0, 0.66, 0.58], skyStrength: 0.55, saturation: -0.05, warmth: 0.35, shadow: 0.30 },
  { hour: 7.5, ambient: [0.97, 0.86, 0.78], sky: [1.0, 0.86, 0.70], skyStrength: 0.75, saturation: 0, warmth: 0.25, shadow: 0.36 },
  { hour: 10, ambient: [1.0, 0.98, 0.94], sky: [1.0, 0.98, 0.90], skyStrength: 0.9, saturation: 0.05, warmth: 0.05, shadow: 0.34 },
  { hour: 13, ambient: [1.0, 1.0, 1.0], sky: [1.0, 1.0, 0.96], skyStrength: 1.0, saturation: 0.08, warmth: 0, shadow: 0.30 },
  { hour: 16, ambient: [1.0, 0.95, 0.86], sky: [1.0, 0.93, 0.78], skyStrength: 0.9, saturation: 0.06, warmth: 0.15, shadow: 0.34 },
  { hour: 18, ambient: [1.0, 0.78, 0.56], sky: [1.0, 0.72, 0.42], skyStrength: 0.8, saturation: 0.08, warmth: 0.55, shadow: 0.40 },
  { hour: 19.5, ambient: [0.70, 0.56, 0.74], sky: [0.95, 0.55, 0.55], skyStrength: 0.5, saturation: -0.1, warmth: 0.1, shadow: 0.24 },
  { hour: 21, ambient: [0.50, 0.56, 0.83], sky: [0.55, 0.65, 1.0], skyStrength: 0.28, saturation: -0.28, warmth: -0.45, shadow: 0.16 },
  { hour: 24, ambient: [0.47, 0.54, 0.82], sky: [0.55, 0.65, 1.0], skyStrength: 0.25, saturation: -0.3, warmth: -0.5, shadow: 0.16 },
];

// Under the rock (docs/23 Y9) there is no sun: "day" is the glow-hours, when the lichen and the
// great fungi shine a dim blue-green, and "night" the deep dark, when the glow dies. Never as bright
// as an open noon, never darker than the floors.
const GLOW: readonly SkyKey[] = [
  { hour: 0, ambient: [0.38, 0.44, 0.62], sky: [0.4, 0.55, 0.7], skyStrength: 0.1, saturation: -0.3, warmth: -0.45, shadow: 0 },
  { hour: 4.5, ambient: [0.40, 0.47, 0.64], sky: [0.4, 0.55, 0.7], skyStrength: 0.1, saturation: -0.28, warmth: -0.42, shadow: 0 },
  { hour: 7, ambient: [0.56, 0.70, 0.74], sky: [0.5, 0.85, 0.8], skyStrength: 0.25, saturation: -0.1, warmth: -0.3, shadow: 0 },
  { hour: 10, ambient: [0.66, 0.82, 0.80], sky: [0.55, 0.95, 0.85], skyStrength: 0.35, saturation: -0.02, warmth: -0.25, shadow: 0 },
  { hour: 15, ambient: [0.68, 0.84, 0.82], sky: [0.55, 0.95, 0.85], skyStrength: 0.35, saturation: 0, warmth: -0.24, shadow: 0 },
  { hour: 18.5, ambient: [0.56, 0.62, 0.76], sky: [0.5, 0.7, 0.85], skyStrength: 0.22, saturation: -0.12, warmth: -0.32, shadow: 0 },
  { hour: 21, ambient: [0.40, 0.46, 0.64], sky: [0.4, 0.55, 0.7], skyStrength: 0.12, saturation: -0.28, warmth: -0.42, shadow: 0 },
  { hour: 24, ambient: [0.38, 0.44, 0.62], sky: [0.4, 0.55, 0.7], skyStrength: 0.1, saturation: -0.3, warmth: -0.45, shadow: 0 },
];

// Under the Ashlands' pall (docs/23 Y10) the sun is a dull copper coin through the ash: "day" is the
// red day, dimmer and warmer than an open noon with soft shadows, and "night" the fire-dark, with no
// moon or star but the land lit red from below by the lava. Never darker than the floors.
const ASH: readonly SkyKey[] = [
  { hour: 0, ambient: [0.62, 0.42, 0.40], sky: [0.9, 0.42, 0.3], skyStrength: 0.2, saturation: -0.18, warmth: 0.35, shadow: 0.06 },
  { hour: 4.5, ambient: [0.62, 0.43, 0.40], sky: [0.9, 0.42, 0.3], skyStrength: 0.2, saturation: -0.18, warmth: 0.35, shadow: 0.06 },
  { hour: 6, ambient: [0.80, 0.52, 0.44], sky: [1.0, 0.5, 0.34], skyStrength: 0.4, saturation: -0.1, warmth: 0.5, shadow: 0.14 },
  { hour: 8, ambient: [0.90, 0.72, 0.62], sky: [1.0, 0.7, 0.5], skyStrength: 0.6, saturation: -0.1, warmth: 0.38, shadow: 0.18 },
  { hour: 13, ambient: [0.96, 0.84, 0.74], sky: [1.0, 0.82, 0.64], skyStrength: 0.72, saturation: -0.08, warmth: 0.3, shadow: 0.2 },
  { hour: 16, ambient: [0.94, 0.78, 0.66], sky: [1.0, 0.76, 0.56], skyStrength: 0.68, saturation: -0.08, warmth: 0.36, shadow: 0.2 },
  { hour: 18, ambient: [0.88, 0.58, 0.46], sky: [1.0, 0.52, 0.32], skyStrength: 0.5, saturation: -0.04, warmth: 0.6, shadow: 0.16 },
  { hour: 19.5, ambient: [0.72, 0.46, 0.42], sky: [0.95, 0.44, 0.3], skyStrength: 0.32, saturation: -0.12, warmth: 0.45, shadow: 0.08 },
  { hour: 21, ambient: [0.63, 0.43, 0.40], sky: [0.9, 0.42, 0.3], skyStrength: 0.22, saturation: -0.16, warmth: 0.36, shadow: 0.06 },
  { hour: 24, ambient: [0.62, 0.42, 0.40], sky: [0.9, 0.42, 0.3], skyStrength: 0.2, saturation: -0.18, warmth: 0.35, shadow: 0.06 },
];

function sky(hour: number, keys: readonly SkyKey[] = SKY): SkyKey {
  const h = ((hour % 24) + 24) % 24;
  let i = 0;
  while (i < keys.length - 2 && keys[i + 1]!.hour <= h) i++;
  const a = keys[i]!, b = keys[i + 1]!;
  const t = (h - a.hour) / (b.hour - a.hour);
  const n = (x: number, y: number) => x + (y - x) * t;
  return { hour: h, ambient: mix(a.ambient, b.ambient, t), sky: mix(a.sky, b.sky, t), skyStrength: n(a.skyStrength, b.skyStrength), saturation: n(a.saturation, b.saturation), warmth: n(a.warmth, b.warmth), shadow: n(a.shadow, b.shadow) };
}

export function phaseOf(hour: number): Phase {
  const h = ((hour % 24) + 24) % 24;
  if (h < 5 || h >= 20.5) return 'night';
  if (h < 8) return 'dawn';
  if (h < 17) return 'day';
  if (h < 19) return 'golden';
  return 'dusk';
}

/** Particle families a climate can ask for; the hour decides which actually fly. */
export interface Atmosphere {
  fireflies?: boolean; motes?: boolean; mist?: boolean; leaves?: boolean; embers?: boolean; drips?: boolean;
  /** Seasonal dressing (docs/23, `game/weather/season.ts`): spring blossom, summer pollen and thistledown, winter ice glints in the air. */
  petals?: boolean; pollen?: boolean; glints?: boolean;
  /** Heat haze: dust motes rising off hot ground (the `heat` weather). */
  rising?: boolean;
}

/** The feel of a region's air, for the seasons and the weather (docs/23): cold country frosts over in winter, snowfields stay white, deserts never drop leaves. */
export type Feel = 'temperate' | 'cold' | 'snowy' | 'arid' | 'tropic' | 'deep' | 'ash';

/** How a region kit is lit (docs/19 N3): open sky or a room, and its own colour of dark. */
export interface Climate {
  setting: Setting;
  /** Indoor rooms: the multiply colour of unlit stone (before the floors). Outdoors: a tint over the sky's ambient. */
  ambient: RGB;
  atmosphere: Atmosphere;
  /** Light the room's own materials give off (glowing crystals, lava), as a colour. */
  glowTint?: RGB;
  /** Outdoors: how the air feels (default temperate). */
  feel?: Feel;
  /** Open country (no canopy): no sun shafts through the trees. */
  open?: boolean;
  /** Under the rock (the Underdark, docs/23 Y9): lit by the glow, not the sun — no sun, moon, shadows, cloud or wind. */
  roofed?: boolean;
  /** Under the Ashlands' pall (docs/23 Y10): a red day through the ash, a night lit red by the lava, no moon. */
  pall?: boolean;
}

export const CLIMATES: Record<string, Climate> = {
  keep: { setting: 'indoor', ambient: [0.50, 0.52, 0.66], atmosphere: { motes: true } },
  cistern: { setting: 'indoor', ambient: [0.44, 0.58, 0.64], atmosphere: { motes: true, drips: true } },
  crypt: { setting: 'indoor', ambient: [0.44, 0.48, 0.68], atmosphere: { mist: true, motes: true } },
  sewer: { setting: 'indoor', ambient: [0.48, 0.56, 0.44], atmosphere: { mist: true, drips: true } },
  brick: { setting: 'indoor', ambient: [0.58, 0.50, 0.48], atmosphere: { motes: true } },
  shrine: { setting: 'indoor', ambient: [0.52, 0.60, 0.50], atmosphere: { motes: true } },
  tomb: { setting: 'indoor', ambient: [0.44, 0.44, 0.56], atmosphere: { mist: true, motes: true } },
  cavern: { setting: 'indoor', ambient: [0.48, 0.47, 0.54], atmosphere: { motes: true, drips: true } },
  temple: { setting: 'indoor', ambient: [0.66, 0.58, 0.46], atmosphere: { motes: true } },
  ember: { setting: 'indoor', ambient: [0.66, 0.46, 0.40], atmosphere: { embers: true }, glowTint: [1.0, 0.45, 0.2] },
  mirewood: { setting: 'outdoor', ambient: [0.96, 1.0, 0.94], atmosphere: { fireflies: true, mist: true, motes: true } },
  forest: { setting: 'outdoor', ambient: [1.0, 1.0, 1.0], atmosphere: { fireflies: true, motes: true } },
  autumn: { setting: 'outdoor', ambient: [1.0, 0.97, 0.92], atmosphere: { leaves: true, fireflies: true } },
  // docs/23 biome kits
  winterwood: { setting: 'outdoor', ambient: [0.95, 0.97, 1.0], atmosphere: { motes: true }, feel: 'snowy', open: true },
  pinewood: { setting: 'outdoor', ambient: [0.97, 1.0, 0.97], atmosphere: { fireflies: true, motes: true, mist: true }, feel: 'cold' },
  dunes: { setting: 'outdoor', ambient: [1.0, 0.97, 0.9], atmosphere: { motes: true }, feel: 'arid', open: true },
  highland: { setting: 'outdoor', ambient: [0.96, 0.97, 1.0], atmosphere: { mist: true, motes: true }, feel: 'cold', open: true },
  jungle: { setting: 'outdoor', ambient: [0.94, 1.0, 0.92], atmosphere: { fireflies: true, mist: true, motes: true }, feel: 'tropic' },
  // docs/23 Y9: glow-worms in the deep dark, spores in the glow, the seep's drips always
  underdark: { setting: 'outdoor', ambient: [0.96, 1.0, 1.0], atmosphere: { fireflies: true, mist: true, motes: true, drips: true }, feel: 'deep', open: true, roofed: true, glowTint: [0.45, 0.95, 0.85] },
  // docs/23 Y10: embers and ash on the air always, the lava's glow, the sky's light through the pall
  ashlands: { setting: 'outdoor', ambient: [1.0, 0.96, 0.92], atmosphere: { embers: true, motes: true }, feel: 'ash', open: true, pall: true, glowTint: [1.0, 0.45, 0.2] },
  // docs/186: the place kits, for a caller that names one (the board itself is lit as its Oryx kit, `oryxKeyOf`:
  // an outdoor place as its country, an indoor one as its nearest hall); outdoor places open to the sky
  town: { setting: 'outdoor', ambient: [1.0, 0.99, 0.96], atmosphere: { motes: true }, open: true },
  docks: { setting: 'outdoor', ambient: [0.97, 0.99, 1.0], atmosphere: { motes: true, mist: true }, open: true },
  courtyard: { setting: 'outdoor', ambient: [1.0, 0.99, 0.96], atmosphere: { motes: true }, open: true },
  graveyard: { setting: 'outdoor', ambient: [0.95, 0.97, 1.0], atmosphere: { mist: true, motes: true }, open: true },
  ruins: { setting: 'outdoor', ambient: [1.0, 1.0, 0.97], atmosphere: { fireflies: true, motes: true }, open: true },
  house: { setting: 'indoor', ambient: [0.62, 0.54, 0.46], atmosphere: { motes: true } },
  ship: { setting: 'indoor', ambient: [0.56, 0.54, 0.52], atmosphere: { motes: true, drips: true } },
  barrow: { setting: 'indoor', ambient: [0.44, 0.46, 0.52], atmosphere: { mist: true, motes: true } },
  mine: { setting: 'indoor', ambient: [0.52, 0.48, 0.44], atmosphere: { motes: true, drips: true } },
};

/** How a kit is lit; an outdoor place with a known country is lit as that country (docs/186 EA14). */
export function climateFor(kitKey: string, country?: string): Climate {
  const own = CLIMATES[kitKey];
  if (own?.setting === 'outdoor' && country && CLIMATES[country] && PLACES_OUTDOOR.has(kitKey)) return CLIMATES[country]!;
  return own ?? CLIMATES['keep']!;
}
const PLACES_OUTDOOR = new Set(['town', 'docks', 'courtyard', 'graveyard', 'ruins']);

/** Where the light comes from, as a unit vector on the board (+x east, +y south) and how long shadows run. */
export interface Sun {
  /** Direction shadows fall (away from the sun), unit vector. */
  dx: number; dy: number;
  /** Shadow length per unit of occluder height, in cells. */
  length: number;
  /** Shadow darkness 0..1. */
  strength: number;
  moon: boolean;
}

export interface DayLight {
  hour: number;
  phase: Phase;
  setting: Setting;
  /** The multiply colour of a square no light reaches (floors applied). */
  ambient: RGB;
  /** Daylight through exits (indoors) — colour already scaled by strength. */
  sky: RGB; skyStrength: number;
  /** Outdoors only. */
  sun?: Sun;
  /** Torch/fire strength: brighter in the dark, 0.55 at noon outdoors. */
  torch: number;
  /** Heroes carry a lantern when the ambient is dark; 0..1. */
  lantern: number;
  saturation: number; warmth: number;
  /** Vignette strength (camera filter). */
  vignette: number;
  /** Extra grade (docs/23): contrast offset (0 none), exposure multiplier (1 none) and a 4×5 colour matrix (row-major, Phaser's layout) for seasonal hue shifts. */
  contrast?: number; exposure?: number; matrix?: readonly number[];
  atmosphere: Required<Atmosphere>;
  /** The climate's feel outdoors (docs/23); indoors undefined. */
  feel?: Feel;
  /** Rime the ground shader lays even under a clear sky, 0..1 of FROST_CAP (a cold country's winter, docs/23). */
  chill?: number;
  /** Air cold enough that breath shows: heroes and monsters puff faint clouds (docs/23). */
  breath?: boolean;
  /** Under the rock (`Climate.roofed`): no cloud shadows, no wind, the seep drips rather than rains. */
  roofed?: boolean;
  /** Under the Ashlands' pall (`Climate.pall`): no moon, and the night's light is the lava's red. */
  pall?: boolean;
}

/** The sun's arc: it rises in the east at 6, stands south at noon, sets in the west at 18. The moon takes the opposite arc. */
function sunAt(hour: number, shadow: number): Sun {
  const h = ((hour % 24) + 24) % 24;
  const day = h >= 6 && h <= 18;
  const t = day ? (h - 6) / 12 : ((h + 6) % 24) / 12;   // 0..1 across its arc
  const a = Math.PI * t;                                  // 0 east → π/2 south → π west
  const elev = Math.max(0.12, Math.sin(a));
  const sx = Math.cos(a), sy = Math.sin(a) * 0.55 + 0.2;  // toward the light (south-leaning: the camera looks north)
  const len = Math.hypot(sx, sy);
  return { dx: -sx / len, dy: -sy / len, length: Math.min(2.6, 0.45 / elev), strength: shadow, moon: !day };
}

/** The lighting of one encounter: (hour, climate) → everything the renderer needs. */
export function dayLight(hour: number, climate: Climate): DayLight {
  const s = sky(hour);
  const phase = phaseOf(hour);
  const dark = 1 - Math.min(1, luma(s.ambient));        // 0 at noon, ~0.5 at night
  const night = phase === 'night' || phase === 'dusk';
  const atm = climate.atmosphere;
  if (climate.setting === 'indoor') {
    return {
      hour: s.hour, phase, setting: 'indoor',
      ambient: legible(climate.ambient),
      sky: s.sky, skyStrength: s.skyStrength,
      torch: 1,
      lantern: 0.55,
      saturation: -0.08, warmth: 0.05,
      vignette: 0.32,
      atmosphere: { fireflies: false, motes: !!atm.motes, mist: !!atm.mist, leaves: false, embers: !!atm.embers, drips: !!atm.drips, petals: false, pollen: false, glints: false, rising: false },
    };
  }
  if (climate.roofed) return glowLight(hour, climate);
  if (climate.pall) return pallLight(hour, climate);
  const ambient = legible([s.ambient[0] * climate.ambient[0], s.ambient[1] * climate.ambient[1], s.ambient[2] * climate.ambient[2]]);
  return {
    hour: s.hour, phase, setting: 'outdoor',
    ambient,
    sky: s.sky, skyStrength: s.skyStrength,
    sun: sunAt(s.hour, s.shadow),
    torch: 0.55 + dark * 0.9,
    lantern: Math.max(0, Math.min(1, (dark - 0.12) * 2.6)),
    saturation: s.saturation, warmth: s.warmth,
    vignette: 0.12 + dark * 0.45,
    atmosphere: {
      fireflies: !!atm.fireflies && (night || phase === 'golden'),
      motes: !!atm.motes && !night,
      mist: !!atm.mist && (phase === 'dawn' || night),
      leaves: !!atm.leaves && !night,
      embers: !!atm.embers,
      drips: false,
      petals: false, pollen: false, glints: false, rising: false,
    },
    feel: climate.feel ?? 'temperate',
  };
}

/** The Ashlands' light under the pall (docs/23 Y10): the sun's arc, but faint shadows, no moon, embers always, lanterns lit early. */
function pallLight(hour: number, climate: Climate): DayLight {
  const s = sky(hour, ASH);
  const phase = phaseOf(hour);
  const dark = 1 - Math.min(1, luma(s.ambient));
  const night = phase === 'night' || phase === 'dusk';
  const atm = climate.atmosphere;
  const sun = sunAt(s.hour, s.shadow);
  return {
    hour: s.hour, phase, setting: 'outdoor',
    ambient: legible([s.ambient[0] * climate.ambient[0], s.ambient[1] * climate.ambient[1], s.ambient[2] * climate.ambient[2]]),
    sky: s.sky, skyStrength: s.skyStrength,
    sun: { ...sun, moon: false },
    torch: 0.7 + dark * 0.8,
    lantern: Math.max(0, Math.min(1, (dark - 0.1) * 2.6)),
    saturation: s.saturation, warmth: s.warmth,
    vignette: 0.2 + dark * 0.4,
    atmosphere: {
      fireflies: false,
      motes: !!atm.motes && !night,
      mist: false,
      leaves: false,
      embers: !!atm.embers,
      drips: false,
      petals: false, pollen: false, glints: false, rising: false,
    },
    feel: climate.feel ?? 'ash',
    pall: true,
  };
}

/** The glow's light under the rock (docs/23 Y9): the hour's phase, but no sun and the lanterns always lit. */
function glowLight(hour: number, climate: Climate): DayLight {
  const s = sky(hour, GLOW);
  const phase = phaseOf(hour);
  const dark = 1 - Math.min(1, luma(s.ambient));        // ~0.22 in the full glow, ~0.5 in the deep dark
  const night = phase === 'night' || phase === 'dusk';
  const atm = climate.atmosphere;
  return {
    hour: s.hour, phase, setting: 'outdoor',
    ambient: legible([s.ambient[0] * climate.ambient[0], s.ambient[1] * climate.ambient[1], s.ambient[2] * climate.ambient[2]]),
    sky: s.sky, skyStrength: s.skyStrength,
    torch: 0.8 + dark * 0.6,
    lantern: Math.max(0.5, Math.min(1, (dark - 0.12) * 2.6)),
    saturation: s.saturation, warmth: s.warmth,
    vignette: 0.3 + dark * 0.3,
    atmosphere: {
      fireflies: !!atm.fireflies && (night || phase === 'dawn'),
      motes: !!atm.motes,
      mist: !!atm.mist && (night || phase === 'dawn'),
      leaves: false, embers: false,
      drips: !!atm.drips,
      petals: false, pollen: false, glints: false, rising: false,
    },
    feel: climate.feel ?? 'deep',
    roofed: true,
  };
}

/**
 * The encounter's hour (N1): the room's own `hour` when the forge stamps one; otherwise a stable
 * hash of the encounter id (every client in the party agrees), else of the board seed.
 */
export function encounterHour(opts: { hour?: number; encounterId?: bigint | number; seed?: number }): number {
  if (opts.hour !== undefined && Number.isFinite(opts.hour)) return ((opts.hour % 24) + 24) % 24;
  const key = opts.encounterId !== undefined ? `enc:${String(opts.encounterId)}` : `seed:${opts.seed ?? 0}`;
  return (cellHash(key, 0, 0, 'hour') % 48) / 2;
}

export function formatHour(hour: number): string {
  const h = Math.floor(hour), m = Math.round((hour - h) * 60);
  return `${String(h % 24).padStart(2, '0')}:${String(m === 60 ? 0 : m).padStart(2, '0')}`;
}
